import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Copy } from 'lucide-react';

/**
 * JsonViewer — lightweight collapsible, syntax-highlighted JSON viewer.
 * No external library required.
 *
 * @param {{ json: string, label?: string }} props
 */
const JsonViewer = ({ json, label = 'View JSON' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  let parsed = null;
  let parseError = false;
  try {
    parsed = JSON.parse(json);
  } catch {
    parseError = true;
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(json);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderValue = (val, depth = 0) => {
    if (val === null) return <span className="text-gray-400">null</span>;
    if (typeof val === 'boolean') return <span className="text-purple-400">{String(val)}</span>;
    if (typeof val === 'number') return <span className="text-blue-400">{val}</span>;
    if (typeof val === 'string') return <span className="text-green-400">"{val}"</span>;
    if (Array.isArray(val)) {
      return (
        <span>
          {'['}
          <div className="ml-4">
            {val.map((item, i) => (
              <div key={i}>
                {renderValue(item, depth + 1)}
                {i < val.length - 1 ? ',' : ''}
              </div>
            ))}
          </div>
          {']'}
        </span>
      );
    }
    if (typeof val === 'object') {
      const entries = Object.entries(val);
      return (
        <span>
          {'{'}
          <div className="ml-4">
            {entries.map(([k, v], i) => (
              <div key={k}>
                <span className="text-red-400">"{k}"</span>
                <span className="text-gray-400">: </span>
                {renderValue(v, depth + 1)}
                {i < entries.length - 1 ? ',' : ''}
              </div>
            ))}
          </div>
          {'}'}
        </span>
      );
    }
    return <span className="text-gray-300">{String(val)}</span>;
  };

  return (
    <div className="mt-2">
      <button
        onClick={() => setIsOpen(o => !o)}
        className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
      >
        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        {label}
      </button>

      {isOpen && (
        <div className="mt-2 relative bg-gray-900 rounded-lg p-3 overflow-auto max-h-64">
          <button
            onClick={handleCopy}
            className="absolute top-2 right-2 text-gray-400 hover:text-white flex items-center gap-1 text-xs transition-colors"
          >
            <Copy size={12} />
            {copied ? 'Copied!' : 'Copy'}
          </button>

          {parseError ? (
            <pre className="text-red-400 text-xs font-mono whitespace-pre-wrap">{json}</pre>
          ) : (
            <pre className="text-xs font-mono text-gray-100 whitespace-pre-wrap">
              {renderValue(parsed)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

export default JsonViewer;
